import {
  AddressResponseData,
  CountriesFactory,
  IAddressData,
  IAddressResponseData,
  ICountriesLookup,
  IStateLookup,
} from "@connox/typescript-library";
import * as Sentry from "@sentry/browser";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {useCustomerContext} from "../../../customer/CustomerContextProvider/CustomerContextProvider";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import "../../../lib/GenericStyles.scss";
import {getShopId} from "../../../lib/getShopId";
import AmazonPayAddressWidget from "../../../lib/payments/AmazonPayAddressWidget";
import window from "../../../lib/WindowExtended";
import ListLoader from "../../../utils/loader/ListLoader";
import Spinner from "../../../utils/loader/Spinner";
import {CheckoutError, DEFAULT_ERROR_MESSAGE, IIsVisible} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import AddressForm, {IFormInfo} from "./AddressForm";
import AddressFormData, {AddressType, IAddressForm} from "./AddressFormData";
import "./AddressStep.scss";
import {ICartRequestHandlerPutCartOptions} from "@connox/typescript-library/types/cart/requestHandlers/CartRequestHandler";
import {isAmazonLoggedIn} from "../../../lib/payments/AmazonPayLibrary";
import {amazonState, isSuccessfulAmazonCheckout} from "../../CheckoutContextProvider/CheckoutViewContextProvider";

interface IAddressesState {
  amazonPayAddress?: IAddressData;
  isInvoiceSameAsDelivery: boolean;
  deliveryFormState?: IFormInfo;
  invoiceFormState?: IFormInfo;
}

const AddressStep: React.FC<IIsVisible> = (props) => {
  const {t} = useTranslation("checkout");
  const {customer} = useCustomerContext();
  const continueText = t("Weiter");
  const {checkout} = useCheckoutContext();
  const {setViewState, viewState} = useCartContext();

  const [addressesState, setAddressesState] = useState<IAddressesState>(() => ({
    isInvoiceSameAsDelivery: isSameAddresses(checkout.deliveryAddress, checkout.invoiceAddress),
  }));

  const [{buttonValue, errors}, setButtonValue] = useState<{buttonValue: string | JSX.Element; errors: string[]}>({
    buttonValue: continueText,
    errors: [],
  });
  const deliveryFormRef = useRef<IAddressForm>(null);
  const invoiceFormRef = useRef<IAddressForm>(null);

  useEffect(() => {
    const errArray: string[] = [];
    checkout.cartData.positions.forEach((pos) => {
      const notAllowedForCountryMsg = pos.messages.find((msg) => msg.code === "NOT_ALLOWED_FOR_COUNTRY");
      if (notAllowedForCountryMsg) {
        const messageToShow = t(notAllowedForCountryMsg.code, {
          itemId: pos.reference?.itemId,
          ns: "cart",
        });
        errArray.push(messageToShow);
      }
    });
    checkout.cartData.positions.some((pos) =>
      pos.messages.some((msg) => {
        if (msg.code === "SHIPPING_NOT_FOUND") {
          errArray.push(
            t(msg.code, {
              country: CountriesFactory.getCountryByCode(checkout.cartData.countryCode)?.name ?? "",
              ns: "cart",
            }),
          );

          return true;
        }

        return false;
      }),
    );

    if (errArray.length) {
      setButtonValue({
        buttonValue: continueText,
        errors: errArray,
      });
    }
  }, [checkout.cartData.positions]);

  useEffect(() => {
    if (!props.isVisible) {
      setButtonValue({buttonValue: continueText, errors: []});

      return;
    }

    // region reset button if there are errors to allow for reposting
    // disclaimer: it's ugly but it seems to be working

    const deliveryAddressErrors = checkout.deliveryAddress?.messages ?? [];
    const invoiceAddressErrors = checkout.invoiceAddress?.messages ?? [];
    const hasErrors = deliveryAddressErrors.length + invoiceAddressErrors.length > 0;

    if (hasErrors) {
      setButtonValue((prevState) => ({...prevState, buttonValue: continueText}));
    }

    // endregion

    // initializing of the address states
    if (addressesState.invoiceFormState && addressesState.deliveryFormState) {
      // only if any address is given, and visibility
      if (checkout.invoiceAddress && checkout.deliveryAddress && props.isVisible) {
        setAddressesState((prevState) => {
          const deliveryFormStateCountries: ICountriesLookup[] = prevState.deliveryFormState?.countries ?? [];
          const deliveryFormStateStates: IStateLookup[] = prevState.deliveryFormState?.states ?? [];
          const invoiceFormStateCountries: ICountriesLookup[] = prevState.invoiceFormState?.countries ?? [];
          const invoiceFormStateStates: IStateLookup[] = prevState.invoiceFormState?.states ?? [];

          const deliveryFormState: IFormInfo | undefined = checkout.deliveryAddress
            ? {
              ...prevState.deliveryFormState,
              address: checkout.deliveryAddress,
              countries: deliveryFormStateCountries,
              states: deliveryFormStateStates,
            }
            : prevState.deliveryFormState;
          const invoiceFormState = checkout.invoiceAddress
            ? {
              ...prevState.invoiceFormState,
              address: checkout.invoiceAddress,
              countries: invoiceFormStateCountries,
              states: invoiceFormStateStates,
            }
            : prevState.invoiceFormState;

          return {
            deliveryFormState,
            invoiceFormState,
            isInvoiceSameAsDelivery: isSameAddresses(checkout.invoiceAddress, checkout.deliveryAddress),
          };
        });
      }
    } else {
      // region helper function to fill in email address and partials from register step

      const fillAddressTemps = (address: AddressResponseData | null): AddressResponseData => {
        const customerEmailIfLoggedIn = customer.isLoggedIn && customer.email ? customer.email : "";

        // checkout addresses if given, with logged-in customer email or invoice email.. if no checkout address given,
        // then dummy, with partial address of view state if new customer is registering, or email of logged-in customer
        let email: string;
        if (address) {
          email = customerEmailIfLoggedIn || viewState.partials?.partialAddress.email || address.email;

          return new AddressResponseData({...address.rawData, email});
        }

        email = viewState.partials?.partialAddress.email || customerEmailIfLoggedIn;

        return new AddressResponseData({
          ...emptyAddrDummy,
          ...viewState.partials?.partialAddress,
          email,
        });
      };

      // endregion helper function

      const invoiceFormStateAddressTemp = fillAddressTemps(checkout.invoiceAddress);
      const deliveryFormStateAddressTemp = fillAddressTemps(checkout.deliveryAddress);

      // prepare last addresses promise with default resolver for given addresses
      let getLastAddressesPromise = new Promise<AddressResponseData[]>((resolve) =>
        resolve([invoiceFormStateAddressTemp, deliveryFormStateAddressTemp]),
      );
      // if no viewState (new customer) and checkout invoice empty, get promise for remote last addresses
      if (!viewState.partials && !checkout.invoiceAddress) {
        getLastAddressesPromise = customer.getLastAddresses(getShopId());
      }

      void getLastAddressesPromise.then(async (addresses) => {
        const deliveryRemote = addresses.find((address) => address.isLastDeliveryAddress);
        const invoiceRemote = addresses.find((address) => address.isLastInvoiceAddress);

        // take remote addresses if given, else checkout addresses or dummy address object
        const invAddr = invoiceRemote || invoiceFormStateAddressTemp;
        const delivAddr = deliveryRemote || deliveryFormStateAddressTemp;

        // get countries
        const countriesTemp = await CountriesFactory.getAllCountries(getShopId());
        // does any address country need states?
        const invoiceCountryNeedsStates =
          countriesTemp.find((country) => country.countryCode === invAddr.countryCode)?.isStateRequired || false;
        const deliveryCountryNeedsStates =
          countriesTemp.find((country) => country.countryCode === delivAddr.countryCode)?.isStateRequired || false;

        const invoiceAddressCountryOrCartCountry = invAddr.countryCode || checkout.cartData.countryCode;
        const deliveryAddressCountryOrCartCountry = delivAddr.countryCode || checkout.cartData.countryCode;
        // get default promise resolvers
        let invStatesPromise = new Promise<IStateLookup[]>((resolve) => resolve([]));
        let delivStatesPromise = new Promise<IStateLookup[]>((resolve) => resolve([]));
        // if states needed, get the real promises
        if (invoiceCountryNeedsStates) {
          invStatesPromise = CountriesFactory.getStates(getShopId(), invoiceAddressCountryOrCartCountry);
        }
        if (deliveryCountryNeedsStates) {
          delivStatesPromise = CountriesFactory.getStates(getShopId(), deliveryAddressCountryOrCartCountry);
        }
        // resolve all state promises
        void Promise.all([invStatesPromise, delivStatesPromise]).then(([invoiceStates, deliveryStates]) => {
          setAddressesState({
            deliveryFormState: {
              address: delivAddr,
              countries: countriesTemp,
              selectedCountry: deliveryAddressCountryOrCartCountry,
              selectedState: delivAddr.state || undefined,
              states: deliveryStates,
            },
            invoiceFormState: {
              address: invAddr,
              countries: countriesTemp,
              selectedCountry: invoiceAddressCountryOrCartCountry,
              selectedState: invAddr.state || undefined,
              states: invoiceStates,
            },
            isInvoiceSameAsDelivery: isSameAddresses(invAddr, delivAddr),
          });
        });
      });
    }
  }, [props.isVisible, checkout.invoiceAddress, checkout.deliveryAddress]);

  if (!props.isVisible || !addressesState.deliveryFormState || !addressesState.invoiceFormState) {
    return null;
  }

  const onAmazonAddress = (aPayAddress: IAddressData) => {
    setButtonValue({buttonValue: continueText, errors: []});
    setAddressesState((prevState) => ({
      ...prevState,
      amazonPayAddress: aPayAddress,
    }));
  };

  const onAmazonError = (errorString: string) => {
    if (errorString !== "Das ausgewählte Lieferland kann leider nicht beliefert werden") {
      Sentry.captureException(new Error(errorString), {
        extra: {
          amazonInfo: {
            accessTokenSet: !!window.accessToken,
            amazonState: amazonState(checkout.paymentId),
            referenceIdSet: !!window.orderReferenceId,
            urlParamsSet: isSuccessfulAmazonCheckout(),
          },
        },
      });
    }
    const knownError = t(errorString, t(DEFAULT_ERROR_MESSAGE));
    setButtonValue({buttonValue, errors: [knownError]});
  };

  /**
   * handle register completion button click in address step
   * @param evt
   */
  const handleRegisterEnd = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    if (!invoiceFormRef.current && !addressesState.amazonPayAddress) {
      return;
    }

    setButtonValue({buttonValue: <Spinner />, errors: []});

    let invoice: IAddressData;
    let delivery: IAddressData;
    if (addressesState.amazonPayAddress) {
      // if user is Amazon Pay customer
      invoice = delivery = addressesState.amazonPayAddress;
    } else if (invoiceFormRef.current) {
      // refObject should always be set (if no aPay address is given)
      invoice = new AddressFormData(invoiceFormRef.current).toJSON();
      delivery = deliveryFormRef.current ? new AddressFormData(deliveryFormRef.current).toJSON() : invoice;
    } else {
      // error, if no address is given in any way
      Sentry.captureException(new Error("No address given at all (most likely amazon pay user)"), {
        extra: {
          place: "address step, button click",
          amazonInfo: {
            loggedIn: isAmazonLoggedIn(),
            urlParamsSet: isSuccessfulAmazonCheckout(),
            amazonState: amazonState(checkout.paymentId),
            referenceIdSet: !!window.orderReferenceId,
          },
        },
      });

      setButtonValue({
        buttonValue: continueText,
        errors: [t(DEFAULT_ERROR_MESSAGE)],
      });

      return;
    }

    const corporate = viewState.partials
      ? viewState.partials.isCorporateCustomer
      : checkout.cartData.isCorporateCustomer;
    const vatNumber = corporate ? invoiceFormRef.current?.VatNumber.value || undefined : undefined;

    try {
      let cart: ICartRequestHandlerPutCartOptions | undefined;
      // cart has to stay undefined, if apay address is
      // given (no isCorporateCustomer and vatId at all)
      // so checkout.paymentId won't be reset
      if (!addressesState.amazonPayAddress && vatNumber) {
        cart = {
          vatNumber,
        };
      }
      await checkout.put({
        address: {
          delivery,
          invoice,
        },
        cart,
      });
      setViewState({isRegistering: viewState.isRegistering, partials: undefined});
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      const errMessage = err.originalError?.response?.data.result.code || err.originalError?.message || err.message;
      setButtonValue({
        buttonValue: continueText,
        errors: [t(errMessage)],
      });
      Sentry.captureException(err);
    }
  };

  /**
   * select country option change handler
   * @param evt
   * @param addressType
   */
  const onCountryChange = async (evt: React.ChangeEvent<HTMLSelectElement>, addressType: AddressType) => {
    const {options, selectedIndex, value} = evt.target;

    let statesArray: IStateLookup[] = [];
    if (options[selectedIndex].dataset.isStateRequired === "true") {
      statesArray = await CountriesFactory.getStates(getShopId(), value);
    }

    if (addressType === AddressType.INVOICE) {
      setAddressesState((prevState: IAddressesState) => {
        if (!prevState.deliveryFormState || !prevState.invoiceFormState) return prevState;

        return {
          deliveryFormState: prevState.isInvoiceSameAsDelivery
            ? {
              ...prevState.deliveryFormState,
              selectedCountry: prevState.invoiceFormState?.selectedCountry,
              selectedState: prevState.invoiceFormState?.selectedState,
            }
            : prevState.deliveryFormState,
          invoiceFormState: {
            ...prevState.invoiceFormState,
            selectedCountry: value,
            selectedState: undefined,
            states: statesArray,
          },
          isInvoiceSameAsDelivery: prevState.isInvoiceSameAsDelivery,
        };
      });
    } else {
      setAddressesState((prevState: IAddressesState) => {
        if (!prevState.deliveryFormState) return prevState;

        return {
          ...prevState,
          deliveryFormState: {
            ...prevState.deliveryFormState,
            selectedCountry: value,
            selectedState: undefined,
            states: statesArray,
          },
        };
      });
    }
  };

  /**
   * select state option change handler
   * @param evt
   * @param addressType
   */
  const onStateChange = (evt: React.ChangeEvent<HTMLSelectElement>, addressType: AddressType) => {
    const {value} = evt.target;
    if (addressType === AddressType.DELIVERY) {
      setAddressesState((prevState: IAddressesState) => {
        if (!prevState.deliveryFormState) return prevState;

        return {
          ...prevState,
          deliveryFormState: {...prevState.deliveryFormState, selectedState: value},
        };
      });
    } else {
      setAddressesState((prevState: IAddressesState) => {
        if (!prevState.invoiceFormState) return prevState;

        return {
          ...prevState,
          invoiceFormState: {...prevState.invoiceFormState, selectedState: value},
        };
      });
    }
  };

  return (
    <React.Suspense
      fallback={
        <div className="AddressStep Mobile-Only-Friendly">
          <ListLoader />
        </div>
      }
    >
      <div className="AddressStep Mobile-Only-Friendly">
        <CheckoutError>{errors}</CheckoutError>
        <h1>{window.orderReferenceId ? t("Ihre Lieferadresse") : t("Ihre Rechnungsadresse")}</h1>
        {window.orderReferenceId && getShopId() === 100001 ? (
          <AmazonPayAddressWidget
            availableCountries={addressesState.invoiceFormState.countries}
            onError={onAmazonError}
            onAddress={onAmazonAddress}
          />
        ) : (
          <React.Fragment>
            <AddressForm
              formInfo={addressesState.invoiceFormState}
              onCountryChange={onCountryChange}
              onStateChange={onStateChange}
              formRef={invoiceFormRef}
              addressType={AddressType.INVOICE}
            />
            <p>
              <input
                type="checkbox"
                id="InvoiceEqualsDelivery"
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  const {checked} = evt.target;
                  setAddressesState((prevState: IAddressesState) => ({
                    ...prevState,
                    isInvoiceSameAsDelivery: checked,
                  }));
                }}
                checked={addressesState.isInvoiceSameAsDelivery}
              />
              <label htmlFor="InvoiceEqualsDelivery">{t("Die Lieferadresse entspricht der Rechnungsadresse")}</label>
            </p>
            <br />
            {!addressesState.isInvoiceSameAsDelivery && (
              <>
                <h1>{t("Ihre Lieferadresse")}</h1>
                {/* <DeliveryTypes /> */}
                <AddressForm
                  formInfo={addressesState.deliveryFormState}
                  onCountryChange={onCountryChange}
                  onStateChange={onStateChange}
                  formRef={deliveryFormRef}
                  addressType={AddressType.DELIVERY}
                />
              </>
            )}
            <p style={{fontSize: ".875rem"}}>{t("* Pflichtfelder")}</p>
          </React.Fragment>
        )}
      </div>
      <div className="Mobile-Only-Friendly Reduced-Line-Height">
        <CartDetails reduced />
        <Button
          action={handleRegisterEnd}
          styles={[ButtonStyles.wide]}
          type={
            typeof buttonValue === "string"
              ? !window.orderReferenceId || !errors.length
                ? ButtonType.default
                : ButtonType.disabled
              : ButtonType.disabled
          }
        >
          {buttonValue}
        </Button>
      </div>
    </React.Suspense>
  );
};

/**
 * @component
 */
export default AddressStep;

export function isSameAddresses(invoice: AddressResponseData | null, delivery: AddressResponseData | null): boolean {
  let isSame = true;
  const keysToBeIgnored = ["addressId", "messages", "isLastInvoiceAddress", "isLastDeliveryAddress"];

  if (invoice && delivery) {
    isSame = Object.entries(invoice.rawData).every(([entryKey]) => {
      if (keysToBeIgnored.includes(entryKey)) {
        return true;
      }

      return delivery.rawData[entryKey] === invoice.rawData[entryKey];
    });
  } else if (!!invoice !== !!delivery) {
    isSame = false;
  }

  return isSame;
}

export const emptyAddrDummy: IAddressResponseData = {
  addressId: "",
  addressSupplement: null,
  city: "",
  company: "",
  countryCode: "",
  dateOfBirth: "",
  department: "",
  email: "",
  firstName: "",
  gender: "F",
  isLastDeliveryAddress: false,
  isLastInvoiceAddress: false,
  lastName: "",
  number: "",
  phone: "",
  state: "",
  street: "",
  title: "",
  zip: "",
};
